/**
 * Anchor Sections (O nama)
 *
 * 1) Opens the matching <details> when the URL hash points to a section id
 *    (e.g. from the mega-menu's deep links), and scrolls it into view.
 *
 * 2) Hides the hover overlay while any section is open (task 4d) — the
 *    per-row open-image (pure CSS, driven by `[open]`) already shows the
 *    picture inline; listens to native `toggle` events on every <details>
 *    and toggles a class on the block root, same technique as
 *    accordion-groups.js.
 *
 * 3) Hover media overlay + mouse-follow (Brief S, 2026-08-27, task 4) —
 *    ported from components/accordion-groups.js (savjeti), which the
 *    brief explicitly points to as the reference implementation. Replaces
 *    the previous "always-visible sticky default image, src-swapped on
 *    hover" approach: now a single shared <img>, absolutely positioned,
 *    invisible (opacity 0, no src) until the first hover of a row that
 *    has its own image — `positionMediaFor()` sets its base `top` to
 *    align with whichever row is hovered, and a continuous rAF + lerp
 *    loop nudges it further towards the live cursor position within that
 *    row (transform only, never top/left, never setInterval). Only wired
 *    up on devices that report real hover + a fine pointer; touch devices
 *    never get the listeners. Fully skipped under
 *    `prefers-reduced-motion: reduce` (the overlay still appears/
 *    disappears on hover in that case, just without the moving parts).
 */
export default function anchorSections() {
	const root = document.querySelector('.anchor-sections');
	if (!root) {
		return;
	}

	const openFromHash = () => {
		const hash = window.location.hash ? window.location.hash.substring(1) : '';
		if (!hash) {
			return;
		}

		const target = document.getElementById(hash);
		if (!target) {
			return;
		}

		if (target.tagName === 'DETAILS') {
			target.open = true;
		}

		const parentDetails = target.closest('details.anchor-sections__section');
		if (parentDetails) {
			parentDetails.open = true;
		}

		window.setTimeout(() => {
			target.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}, 50);
	};

	window.addEventListener('hashchange', openFromHash);
	openFromHash();

	// --- Hide the hover overlay while anything is open (§2) ---
	const updateOpenState = () => {
		const anyOpen = !!root.querySelector('.anchor-sections__section[open]');
		root.classList.toggle('anchor-sections--has-open', anyOpen);
	};

	root.querySelectorAll('.anchor-sections__section').forEach((details) => {
		details.addEventListener('toggle', updateOpenState);
	});
	updateOpenState(); // covers the hash-open case above, which sets `.open` before this runs

	// --- Hover media overlay + mouse-follow (§3) ---
	const mediaContainer = root.querySelector('.anchor-sections__media');
	const mediaImg = root.querySelector('.anchor-sections__media-img');
	const rowContainer = root.querySelector('.anchor-sections__row');
	const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

	if (!mediaContainer || !mediaImg || !rowContainer || !canHover) {
		return;
	}

	const rows = root.querySelectorAll('.anchor-sections__section--has-image[data-hover-image]');
	if (!rows.length) {
		return;
	}

	// Base position: align the absolutely-positioned overlay with the
	// vertical center of whichever row is hovered, clamped so it never
	// sits above the top or spills below the bottom of the whole list.
	// Computed fresh on every `mouseenter` from live
	// `getBoundingClientRect()` values (both viewport-relative, so their
	// difference is scroll-independent).
	const positionMediaFor = (row) => {
		const rowRect = row.getBoundingClientRect();
		const containerRect = rowContainer.getBoundingClientRect();
		const mediaRect = mediaContainer.getBoundingClientRect();

		let top = rowRect.top - containerRect.top + rowRect.height / 2 - mediaRect.height / 2;
		const maxTop = Math.max(0, containerRect.height - mediaRect.height);
		top = Math.max(0, Math.min(top, maxTop));

		mediaContainer.style.top = `${top}px`;
	};

	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	// Task 4c: more swing than the savjeti reference (AMPLITUDE 32 / LERP
	// 0.07 there, tuned round 4 the same brief round) and a softer trail —
	// bigger amplitude, lower lerp factor (each frame closes less of the
	// remaining distance, so the image trails the cursor longer).
	const AMPLITUDE = 36;
	const LERP = 0.06;

	let targetX = 0;
	let targetY = 0;
	let currentX = 0;
	let currentY = 0;
	let rafId = null;
	let activeRow = null;

	const applyTransform = () => {
		currentX += (targetX - currentX) * LERP;
		currentY += (targetY - currentY) * LERP;
		mediaImg.style.transform = `translate3d(${currentX.toFixed(2)}px, ${currentY.toFixed(2)}px, 0)`;
		rafId = window.requestAnimationFrame(applyTransform);
	};

	const startTracking = () => {
		if (rafId === null) {
			rafId = window.requestAnimationFrame(applyTransform);
		}
	};

	const stopTracking = () => {
		if (rafId !== null) {
			window.cancelAnimationFrame(rafId);
			rafId = null;
		}
		targetX = 0;
		targetY = 0;
		currentX = 0;
		currentY = 0;
		mediaImg.style.transform = '';
	};

	// Both axes (task 4c: "u svim pravcima, ne samo horizontalno") — the
	// cursor's position within the hovered row maps to a target X *and* Y
	// offset, each independently clamped to ±AMPLITUDE.
	const handleMouseMove = (event) => {
		if (!activeRow) {
			return;
		}
		const rect = activeRow.getBoundingClientRect();
		const relX = (event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
		const relY = (event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
		targetX = Math.max(-1, Math.min(1, relX)) * AMPLITUDE;
		targetY = Math.max(-1, Math.min(1, relY)) * AMPLITUDE;
	};

	rows.forEach((row) => {
		const hoverSrc = row.getAttribute('data-hover-image');

		row.addEventListener('mouseenter', () => {
			activeRow = row;
			positionMediaFor(row);
			if (mediaImg.getAttribute('src') !== hoverSrc) {
				mediaImg.src = hoverSrc;
			}
			mediaImg.classList.add('is-visible');

			if (!reduceMotion) {
				row.addEventListener('mousemove', handleMouseMove);
				startTracking();
			}
		});

		row.addEventListener('mouseleave', () => {
			activeRow = null;
			mediaImg.classList.remove('is-visible');

			if (!reduceMotion) {
				row.removeEventListener('mousemove', handleMouseMove);
				stopTracking();
			}
		});
	});
}
